import { useTu } from "@src/appV2/i18n/useTu";
import type { TuFunction } from "@src/appV2/i18n/types";
import { Box, type Theme } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { type UseModalState } from "@src/lib/ui-react";
import { InfoBanner } from "../../../components/InfoBanner/InfoBanner";
import { IssueRow } from "./IssueRow";
import { getWarningContent } from "./inviteIssueContent";
import { type InviteErrorReason, type InviteWarningReason } from "./types";
import { fallbackTu } from "@src/appV2/i18n/fallbackTu";

/**
 * Resolve the per-reason banner message for a single error.
 * Honors the `WORKER_CANT_CLAIM_SHIFT` backend `detail` override.
 */
export function getErrorMessage(
  errorReasons: InviteErrorReason[],
  detail?: string,
  tu: TuFunction = fallbackTu
): string {
  if (errorReasons.length !== 1) {
    return tu("This professional cannot receive this invite due to multiple issues.");
  }

  if (errorReasons[0] === "WORKER_CANT_CLAIM_SHIFT" && detail) {
    return detail;
  }

  return getErrorMessageForReason(errorReasons[0], tu);
}

interface InviteValidationResultsModalProps {
  modalState: UseModalState;
  errorReasons: InviteErrorReason[];
  warningReasons?: InviteWarningReason[];
  missingDocuments?: string[];
  /** Backend detail message to display instead of the generic fallback. */
  detail?: string;
  professionalName?: string;
  qualificationName?: string;
  /**
   * When provided, the primary action lets the user pick a different professional.
   * When absent (preselected-worker flow), only a "Close" action is shown.
   */
  onSelectAnother?: () => void;
  onDismiss: () => void;
}

/**
 * Modal displayed when invite validation surfaces blocking errors and/or
 * non-blocking warnings. Renders every reason at once so the user sees the
 * full picture in a single view.
 */
export function InviteValidationResultsModal(props: InviteValidationResultsModalProps) {
  const { tu } = useTu("schedule");

  const {
    modalState,
    errorReasons,
    warningReasons = [],
    missingDocuments,
    detail,
    professionalName,
    qualificationName,
    onSelectAnother,
    onDismiss,
  } = props;

  const isBlockedError = errorReasons.includes("WORKER_BLOCKED");

  // Workplace/qualification issues aren't about the selected professional —
  // selecting another won't resolve them. Suppress the professional-centric
  // chrome (banner title, primary action, helper text) when that's the only
  // reason.
  const isWorkplaceQualificationIssue =
    errorReasons.length === 1 &&
    errorReasons[0] === "QUALIFICATION_NOT_ENABLED" &&
    warningReasons.length === 0;

  const handleSelectAnother = () => {
    modalState.closeModal();
    onSelectAnother?.();
  };

  const handleDismiss = () => {
    modalState.closeModal();
    onDismiss();
  };

  const hasMissingDocuments =
    errorReasons.includes("WORKER_MISSING_REQUIREMENTS") &&
    missingDocuments !== undefined &&
    missingDocuments.length > 0;

  const bannerTitle = isWorkplaceQualificationIssue
    ? getWorkplaceQualificationIssueTitle({ qualificationName, tu })
    : getInviteErrorBannerTitle({ professionalName, tu });

  // A single blocking error with no warnings renders as one banner.
  // Any combination of multiple errors or any warnings alongside renders
  // each reason as its own row so the user sees every issue at once.
  const isSingleItem = errorReasons.length === 1 && warningReasons.length === 0;
  const summaryMessage = isSingleItem
    ? getErrorMessage(errorReasons, detail, tu)
    : tu("This professional cannot receive this invite. Please review the issues below.");

  let footerHelperText = tu(
    "Re-enable this qualification in your workplace settings, or choose a different qualification for this shift."
  );

  if (!isWorkplaceQualificationIssue) {
    footerHelperText = onSelectAnother
      ? tu("Please select another professional to invite.")
      : tu("This professional cannot receive this invite.");
  }

  return (
    <ResponsiveDialog
      dismissible={false}
      modalState={modalState}
      showCloseButton={!isBlockedError}
      title={tu("Unable to send invite")}
      footerContent={
        isWorkplaceQualificationIssue || !onSelectAnother ? (
          <DialogFooter
            layout="horizontal"
            primaryAction={{
              label: tu("Close"),
              onClick: handleDismiss,
            }}
          />
        ) : (
          <DialogFooter
            layout="horizontal"
            primaryAction={{
              label: tu("Select another professional"),
              onClick: handleSelectAnother,
            }}
            secondaryAction={
              isBlockedError
                ? undefined
                : {
                    label: tu("Close"),
                    onClick: handleDismiss,
                  }
            }
          />
        )
      }
      onClose={isBlockedError ? () => {} : handleDismiss}
    >
      <Box
        sx={(theme: Theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(4),
        })}
      >
        <InfoBanner message={summaryMessage} title={bannerTitle} variant="error" />

        {!isSingleItem && (
          <Box
            sx={(theme: Theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(2),
            })}
          >
            {errorReasons.map((reason) => (
              <IssueRow
                key={`error-${reason}`}
                severity="error"
                title={getErrorTitleForReason(reason, tu)}
                description={getErrorMessageForReason(reason, tu, { detail })}
              />
            ))}
            {warningReasons.map((warning) => {
              const { title, message } = getWarningContent(warning, tu);
              return (
                <IssueRow
                  key={`warning-${warning.type}`}
                  severity="warning"
                  title={title}
                  description={message}
                />
              );
            })}
          </Box>
        )}

        {hasMissingDocuments && (
          <Box
            sx={(theme: Theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(1),
            })}
          >
            <Text semibold sx={(theme) => ({ color: theme.palette.text.primary })} variant="body2">
              {tu("Missing documents:")}
            </Text>
            <Box
              component="ul"
              sx={(theme: Theme) => ({
                margin: 0,
                paddingLeft: theme.spacing(4),
              })}
            >
              {missingDocuments.map((document) => (
                <Box
                  key={document}
                  component="li"
                  sx={(theme: Theme) => ({
                    color: theme.palette.text.secondary,
                  })}
                >
                  <Text variant="body2">{document}</Text>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Text
          sx={(theme: Theme) => ({
            color: theme.palette.text?.secondary,
          })}
          variant="body2"
        >
          {footerHelperText}
        </Text>
      </Box>
    </ResponsiveDialog>
  );
}

function getErrorMessageForReason(
  reason: InviteErrorReason,
  tu: TuFunction,
  options: { detail?: string } = {}
): string {
  switch (reason) {
    case "SHIFT_OVERWORKING":
      return tu(
        "This invite cannot be sent because the worker would exceed the mandatory 17-hour limit."
      );
    case "SHIFT_IN_PAST":
      return tu("This shift has already started.");
    case "WORKER_MISSING_LICENSE":
      return tu("This professional is missing a required license.");
    case "WORKER_MISSING_DOCUMENTS":
      return tu("This professional is missing required documents.");
    case "WORKER_MISSING_REQUIREMENTS":
      return tu("This professional is missing required documents for this shift.");
    case "WORKER_DISABLED_TEST_ACCOUNT":
      return tu("This is a disabled test account.");
    case "WORKER_CANT_CLAIM_SHIFT":
      return options.detail ?? tu("This professional cannot claim this shift.");
    case "WORKER_PAYMENTS_DISABLED":
      return tu("This professional has payments disabled.");
    case "WORKER_PENDING_DOCUMENTS":
      return tu("This professional has documents pending review.");
    case "WORKER_UNSIGNED_AGREEMENT":
      return tu("This professional hasn't signed the required agreement.");
    case "WORKER_DEACTIVATED":
      return tu("This professional's account has been deactivated.");
    case "WORKER_BLOCKED":
      return tu("This professional has been blocked by your workplace.");
    case "FACILITY_SUSPENDED":
      return tu("This facility has been suspended.");
    case "WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK":
      return tu("This professional is missing data for background check.");
    case "WORKER_HAS_REJECTED_BACKGROUND_CHECK":
      return tu("This professional has a rejected background check.");
    case "QUALIFICATION_NOT_ENABLED":
      return tu(
        "This qualification is hidden from your workplace's calendar. Re-enable it in Settings or choose another qualification."
      );
    case "SHIFT_DUPLICATE_INVITE":
      return tu(
        "This professional already has a booked shift with the exact same details at this workplace."
      );
  }
}

function getErrorTitleForReason(reason: InviteErrorReason, tu: TuFunction): string {
  switch (reason) {
    case "SHIFT_OVERWORKING":
      return tu("Exceeds 17-hour limit");
    case "SHIFT_IN_PAST":
      return tu("Shift already started");
    case "WORKER_MISSING_LICENSE":
      return tu("Missing license");
    case "WORKER_MISSING_DOCUMENTS":
      return tu("Missing documents");
    case "WORKER_MISSING_REQUIREMENTS":
      return tu("Missing shift requirements");
    case "WORKER_DISABLED_TEST_ACCOUNT":
      return tu("Disabled test account");
    case "WORKER_CANT_CLAIM_SHIFT":
      return tu("Cannot claim shift");
    case "WORKER_PAYMENTS_DISABLED":
      return tu("Payments disabled");
    case "WORKER_PENDING_DOCUMENTS":
      return tu("Documents pending review");
    case "WORKER_UNSIGNED_AGREEMENT":
      return tu("Unsigned agreement");
    case "WORKER_DEACTIVATED":
      return tu("Account deactivated");
    case "WORKER_BLOCKED":
      return tu("Blocked by workplace");
    case "FACILITY_SUSPENDED":
      return tu("Workplace suspended");
    case "WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK":
      return tu("Missing background check data");
    case "WORKER_HAS_REJECTED_BACKGROUND_CHECK":
      return tu("Rejected background check");
    case "QUALIFICATION_NOT_ENABLED":
      return tu("Qualification disabled");
    case "SHIFT_DUPLICATE_INVITE":
      return tu("Duplicate invite");
  }
}

function getWorkplaceQualificationIssueTitle(options: {
  qualificationName: string | undefined;
  tu: TuFunction;
}): string {
  const { qualificationName, tu } = options;

  if (qualificationName === undefined) {
    return tu("Cannot post invite for this qualification");
  }

  return tu("Cannot post invite for {{qualificationName}} qualification", {
    values: { qualificationName },
  });
}

function getInviteErrorBannerTitle(options: {
  professionalName: string | undefined;
  tu: TuFunction;
}): string {
  const { professionalName, tu } = options;

  if (professionalName === undefined) {
    return tu("Unable to invite this professional");
  }

  return tu("Unable to invite {{professionalName}}", { values: { professionalName } });
}
